/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 * This is a module that provides configuration and run initialization for our AngularJs module.
 */

/**
 * This is our app's config block. Config blocks are ran first, then run blocks.
 * More information could be found here: https://docs.angularjs.org/guide/module#dependencies-and-order-of-execution
 *
 * @param $locationProvider
 * @param $logProvider
 * @param $provide
 * @param $stateProvider
 * @param $urlRouterProvider
 * @param $urlMatcherFactoryProvider
 * @param $sceDelegateProvider
 * @param uiSelectConfig
 * @param CONFIG
 */

export const angularjsAppConfig = [
  '$locationProvider', '$logProvider', '$provide', '$stateProvider', '$urlRouterProvider',
  '$urlMatcherFactoryProvider', '$sceDelegateProvider', 'uiSelectConfig', 'CONFIG',
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  angularjsAppConfigFn,
];

export const angularRunBlock = [
  '$anchorScroll', '$log', '$rootScope', '$state', '$stateParams', '$window',
  'Restangular', 'BulbAuthService', 'BulbBrowserService', 'BulbErrorService', 'BulbUtilitiesService', 'ErrorService',
  'LoggedOutService', 'CONFIG',
  angularRunBlockFn,
];

function angularjsAppConfigFn(
  $locationProvider,
  $logProvider,
  $provide,
  $stateProvider,
  $urlRouterProvider,
  $urlMatcherFactoryProvider,
  $sceDelegateProvider,
  uiSelectConfig,
  CONFIG,
) {
  // Set the urls to be case insensitive:
  $urlMatcherFactoryProvider.caseInsensitive(true);

  // Setting up custom ui-router url Matcher type to prevent issues with /'s and ~'s.
  // This is identical to the standard ':any' type, except this only parses up to the first slash, rather than
  // encoding slashes.  We use this instead of ':string' because ':string' encodes ~'s by duplicating them.
  $urlMatcherFactoryProvider.type('pathVariable', {
    encode: angular.identity,
    decode: angular.identity,
    equals: angular.equals,
    pattern: /[^/]*/,
  });

  // Set this to true to enable debug logging.
  $logProvider.debugEnabled(false);
  $locationProvider.html5Mode(true);
  $locationProvider.hashPrefix('!'); // for browsers without HTML5 URL support

  // remove problematic trailing slashes
  $urlRouterProvider.rule(($injector, $location) => {
    const path = $location.path();

    // check to see if the path ends in '/'
    if (path && path[path.length - 1] === '/') {
      return path.substring(0, path.length - 1);
    }
    return '';
  });

  // Decorate $location with a .skipReload() function which will prevent reloading (ie, state change) following the
  // next $location update.
  $provide.decorator('$location', ['$delegate', '$rootScope', ($delegate, $rootScope) => {
    let skippingReload = false;

    $rootScope.$on('$locationChangeSuccess', (event) => {
      if (skippingReload) {
        event.preventDefault();
        skippingReload = false;
      }
    });

    // eslint-disable-next-line no-param-reassign
    $delegate.skipReload = function skipReload() {
      skippingReload = true;
      return this;
    };

    return $delegate;
  }]);

  /*
   * This workaround is necessary because when the angularjs $location instance is created, it parses the current
   * address of the page.  It requires that the address is prefixed with the <base href="..."/> set by index.html.
   * However, we use that base href to make sure all asset resources are located under the bulb-ngclient folder, but
   * we obviously don't want that to be the prefix of all server paths.  This had to be added when angular deprecated
   * the deployURL build option.  This is the workaround since we have to work with angular, but we are still using
   * ajs $location for routing.  We are basically short-circuiting the $browser.baseHref() to always return '/' rather
   * than what is configured in index.html.  $browser is considered a private API, but this was the simplest way of
   * getting the routing to work without significant cloudFront or rewrite rule changes or alternative modification
   * so tha ajs code.
   */
  $provide.decorator('$browser', ['$delegate', ($delegate) => {

    // eslint-disable-next-line no-param-reassign
    $delegate.baseHref = function replacement() {
      return '/';
    };

    return $delegate;
  }]);

  $sceDelegateProvider.resourceUrlWhitelist([
    // Allow same origin resource loads.
    'self',
    // Allow loading from our cloudfront server
    '**',
    // Allow urls that point to the download server
    `${CONFIG.aws.downloadServer}/**`,
  ]);

  $stateProvider
  // NOTE : this root state is only ever displayed in the dev environment.  On prod and staging, the 'external'
  // landing page is loaded instead, and this state is inaccessible, other than as the parent of other states.
    .state('root', {
      url: '/?verifyEmail',
      resolve: {
        BulbAuthService: 'BulbAuthService',
        currentUser: ['BulbAuthService', (BulbAuthService) => BulbAuthService.currentUser],
        $title: () => null,
        translateReady: ['$translate', ($translate) => $translate.onReady()],
      },
      templateUrl: 'ajs/app.html',
      controller: 'ApplicationController as bulbAppCtrl',
    })

  // State to handle /b/home like the old client.  If logged in, it goes to the author profile, otherwise, go
  // to the sign-in page.  The actual routing is handled via the '$stateChangeStart' handler below.
    .state('home', {
      parent: 'root',
      url: '^/b/home',
    })

  // The 'bulbView' state provides a styled (i.e. centered, padded) wrapper <div class="bulb-view"> tag
  // to avoid requiring a top-level <div class="bulb-view> in every template.  States requiring more
  // control over their horizontal layout (e.g. composite views) can continue to use 'root' as their parent.
    .state('bulbView', {
      parent: 'root',
      abstract: true,
      views: {
        content: {
          template: '<div class="bulb-view" ui-view="bulbView"></div>',
        },
        'header@root': {
          template: '<bulb-header></bulb-header>',
        },
      },
    })

    .state('blockingAccess', {
      parent: 'bulbView',
      url: '^/b/blockingAccess?error',
      views: {
        // We need to override the header as if this state is hit, we don't want to allow the user to continue
        // using the application.
        'header@root': {
          templateUrl: 'ajs/components/auth/bulb-header.html',
          controller: 'BulbHeaderController as bulbHeaderCtrl',
        },
        bulbView: {
          template: '<div class="text-center">'
            + '<h1 translate>error_blocking_access</h1><h3 translate>error_blocking_access_details</h3>'
            + '<h5>{{$stateParams.error}}</h5>'
            + '<a translate href="https://help.bulbapp.com/hc/en-us">sign_in_unsuccessful_link</a>'
            + '</div>',
        },
      },
    })

  // DO LATER : the next two states should probably be moved to the error module.
    .state('notFound', {
      parent: 'bulbView',
      url: '^/b/404?errorTitleCode&errorDescriptionCode',
      resolve: {
        title: ['$stateParams',
          ($stateParams) => (($stateParams.errorTitleCode)
            ? $stateParams.errorTitleCode : 'error.404.title')],
        description: ['$stateParams',
          ($stateParams) => (($stateParams.errorDescriptionCode)
            ? $stateParams.errorDescriptionCode : 'error.404.description')],
      },
      views: {
        bulbView: {
          template:
            '<bulb-error error-code="404"'
            + 'error-title="{{$resolve.title}}"'
            + 'error-description="{{$resolve.description}}"></bulb-error>',
        },
      },
    })
    .state('accessDenied', {
      parent: 'bulbView',
      url: '^/b/403?errorTitleCode&errorDescriptionCode',
      resolve: {
        title: ['$stateParams',
          ($stateParams) => (($stateParams.errorTitleCode)
            ? $stateParams.errorTitleCode : 'error.403.title')],
        description:
          ['$stateParams',
            ($stateParams) => (($stateParams.errorDescriptionCode)
              ? $stateParams.errorDescriptionCode : 'error.403.description')],
      },
      views: {
        bulbView: {
          // DO LATER : specify the targetUrl when going to the 'signIn' sate so the user returns to it
          // after sign in
          template:
            '<bulb-error error-code="403" '
            + 'error-title="{{$resolve.title}}"'
            + 'error-description="{{$resolve.description}}"></bulb-error>',
        },
      },
    })
    .state('error', {
      parent: 'bulbView',
      url: '^/b/error?errorCode&errorMessage',
      resolve: {
        $title: ['$translate', ($translate) => $translate('server_error_title')],
      },
      views: {
        bulbView: {
          // DO LATER : this is a simple generic error page for use until we have a cleaner, more
          // user friendly manner of displaying errors.
          template: '<h1>{{$title}}</h1>'
            + '<div ng-if="$stateParams.errorCode" translate>{{$stateParams.errorCode}}</div>'
            + '<div ng-if="$stateParams.errorMessage" translate>{{$stateParams.errorMessage}}</div>',
        },
      },
    })
    .state('ltiLaunchError', {
      parent: 'bulbView',
      url: '^/b/lti/launch/{organizationId}',
      // This is the launch url, and should never be seen by a user except if there is an error, because if
      // if the launch succeeds, the user is redirected to their profile page.
      resolve: {
        $title: ['$translate', ($translate) => $translate('lti_launch_error_title')],
      },
      views: {
        bulbView: {
          controller: ['$window', function ($window) {
            this.failureMessage = $window.bulbRootModel.ltiFailureMessage;
          }],
          controllerAs: 'ltiLaunchErrorCtrl',
          template: '<h1>{{$title}}</h1>'
            + '<div>{{ltiLaunchErrorCtrl.failureMessage}}</div>',
        },
      },
    });

  // Unknown paths go to 404 page.
  $urlRouterProvider.otherwise(($injector) => {
    $injector.get('$state').go('notFound', {}, { location: false });
  });

  uiSelectConfig.theme = 'select2';
  uiSelectConfig.searchEnabled = false;
}

/**
 * This is our app's run initialization function.
 *
 * Run blocks are the closest thing in AngularJS to the main method. A run block is the code which needs to run to
 * kickstart the application. It is executed after all of the services have been configured and the injector has been
 * created. Run blocks typically contain code which is hard to unit-test, and for this reason should be declared in
 * isolated modules, so that they can be ignored in the unit-tests.
 */
function angularRunBlockFn(
  $anchorScroll,
  $log,
  $rootScope,
  $state,
  $stateParams,
  $window,
  Restangular,
  BulbAuthService,
  BulbBrowserService,
  BulbErrorService,
  BulbUtilitiesService,
  ErrorService,
  LoggedOutService,
  CONFIG,
) {
  // Add CONFIG to the $rootScope here since we can't easily do it in the module since it is generated.
  $rootScope.CONFIG = CONFIG;

  // Add 'Foundation' to the root scope so that it can be accessed by templates.
  // @ts-ignore
  $rootScope.Foundation = Foundation;

  // Add HOSTNAME to the $rootScope here so there is no need to hardcode the domain name(www.bulbapp.com) in the application.
  $rootScope.HOST_NAME = $window.location.hostname;

  // We are now programmatically removing any residual the service-worker from our app's domain.
  if ('serviceWorker' in navigator) {
    // Grab any residual registered serive worker of ours from within our domain and unregister it.
    // Some background and context: Ther ServiceWorkerAPI provdies an interface called 'ServiceWorkerContainer'
    // Which provides an object representing the service worker as an overall unit in the network ecosystem.
    // It is within this container that we grab *our* service worker and not any other potential service worker
    // Registered within the user's browser. This is what we wanted because we wouldn't potentially want to unregister
    // A service worker that another domain registered.
    // If we wanted to, we could also use the '.getRegistration()' method and pass it the scope URL of our specific
    // Service worker -- however this is not necessary in our instance due to the reasons stated above.
    window.navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((currentRegisteredServiceWorker) => {
        currentRegisteredServiceWorker.unregister().then((unregisterd) => {
          console.log(unregisterd ? 'Successfully unregistered our service worker!'
            : 'Unable to unregister our service worker.');
        });
      });
    });
  }

  /**
   * True after the site visit has been tracked, to ensure the site visit is only tracked once per visit.
   * @type {boolean}
   */
  const siteVisitTracked = false;

  // Apply foundation to all new html elements
  $rootScope.$on('$viewContentLoaded', () => {
    // @ts-ignore
    jQuery(document).foundation();
  });

  // Add bulb-browser attribute to HTML tag, enabling CSS access to browser name and version.
  jQuery(document.documentElement).attr('bulb-browser', BulbBrowserService.getBrowserAttributes());

  /**
   * True if a state change is in progress. We check this in the locationChangeStart
   * function to prevent infinite redirects.
   * @type {boolean}
   *
   * FUTURE: This is part of $rootScope so it can be accessed by the bulbHybrid directive.  Once that directive is
   * no longer necessary, this can be changed back to a local variable.
   */
  $rootScope.changingState = false;

  $rootScope.$on('$stateChangeStart', (event, nextState, nextStateParams) => {
    $rootScope.changingState = true;

    // FUTURE: This url is added to the scope to handle a race condition with the bulbHybrid directive when it
    // forces a page reload.  It can be removed when that directive is no longer necessary.
    $rootScope.pendingUrl = $state.href(nextState, nextStateParams, { absolute: true });

    // If the page has an initial error specified, it has been loaded in the context of an error.
    // Rather than proceeding to the state specified by the url, show the appropriate error state instead.
    // bulbRootModel is populated as part of the bulbRootScript that is injected into index.html by the server.
    // However, if we are already heading to the error state, skip this, so we don't overwrite any incoming messages
    // or codes.
    if (nextState.name !== 'error' && window.bulbRootModel && window.bulbRootModel.errorStatus) {
      event.preventDefault();

      const { errorStatus } = window.bulbRootModel;

      // clear out the error information to prevent it from showing the error on the next state change:
      delete window.bulbRootModel.errorStatus;

      // FUTURE : It would be nice to simplify how we get to the error views rather than having switch cases
      // like here and down below.  Additional information about what failed could also be displayed which might
      // help us track down information if a user were to contact us.
      switch (errorStatus) {
        case 403:
          $state.go('accessDenied', window.bulbRootModel, { location: false });
          break;
        case 404:
          $state.go('notFound', window.bulbRootModel, { location: false });
          break;
        default:
          if (window.bulbRootModel.errorMessage) {
            $state.go('error', { errorMessage: window.bulbRootModel.errorMessage }, { location: false });
          } else {
            $state.go('error', { errorCode: 'unknown_error' }, { location: false });
          }
          break;
      }
    }

    $log.debug('$stateChangeStart', nextState, nextStateParams);
    if (nextState.name === 'root' || nextState.name === 'home') {
      event.preventDefault();
      if (CONFIG.env.hasLandingPage === true) {
        // Rather than displaying the root state, we reload so our normal 'external' landing page is loaded.
        $window.location.href = '/';
      } else
        if (BulbAuthService.currentUser.isAuthenticated()) {
          $state.go('author', { username: BulbAuthService.currentUser.username }, { location: 'replace' });
        } else {
          $state.go('signIn', { location: 'replace' });
        }
    }
  });

  // State change success event handler:
  // Reset state changing status, track site visit (only the first time), and  track page view.
  $rootScope.$on('$stateChangeSuccess', (event, toState, toParams, prevState, prevStateParams) => {
    // make sure we scroll to any specified anchor, or top of page if there isn't one.
    $anchorScroll();

    $rootScope.changingState = false;
    delete $rootScope.pendingUrl;

    $log.debug('$stateChangeSuccess', toState, toParams);

    // Include any states in this array that you don't want to navigate to when hitting the "back arrow" on a
    // user-created unit page.
    const unitStates = ['unit', 'unit.page', 'preview_page', 'live_page', 'presentation_page', ''];

    if (unitStates.indexOf(prevState.name) === -1) {
      $state.previousState = { name: prevState.name, params: prevStateParams };
    }
  });

  // Report state change error
  $rootScope.$on('$stateChangeError', (event, toState, toParams, fromState, fromParams, error) => {
    if (error) {
      event.preventDefault();
      switch (error.status) {
        case 403:
          $state.go('accessDenied', {}, { location: false });
          break;
        case 404:
          $state.go('notFound', {}, { location: false });
          break;
        // DO LATER : add other response statuses here, such as 500.
      }
    } else {
      const message = `$stateChangeError: from "${fromState.name}" to "${toState.name}" ${
        JSON.stringify(toParams)}`;
      BulbErrorService.error(message, error);
    }
  });

  // Prevent a redirect loop
  $rootScope.$on('$locationChangeStart', (event, next) => {
    // Check to see if the next url is a known external url reference
    // FUTURE: If this array ends up getting extended use, we should probably move this to a config somewhere.
    const urlExceptions = ['/contact.html'];

    for (let i = 0; i < urlExceptions.length; i++) {
      // 'next' is an absolute path, so prefix our exception elements with our current path prefix
      if (next === $window.location.origin + urlExceptions[i]) {
        $window.location.href = next;
        event.preventDefault();
        return;
      }
    }

    if ($rootScope.changingState) {
      event.preventDefault();
    }
  });

  // added to make $state and $stateParams accessible to all views for state transitions or param access
  $rootScope.$state = $state;
  $rootScope.$stateParams = $stateParams;

  Restangular.setBaseUrl('/b/rest');
  Restangular.setDefaultHttpFields({ cache: false });
  Restangular.setErrorInterceptor((response) => ErrorService.interceptRestangularError(response));
  // This should be registered for all of our calls, including angular http services, but it is only here currently.
  Restangular.addResponseInterceptor(BulbUtilitiesService.watchVersionsOnRequests.bind(BulbUtilitiesService));
  Restangular.addResponseInterceptor(LoggedOutService.watchLoggedInStatusOnRequests.bind(LoggedOutService));
  Restangular.setErrorInterceptor(LoggedOutService.watchLoggedInStatusOnError.bind(LoggedOutService));
}

/**
 * Values we set for JqueryUi.
 */
const jqueryUiConfig = {
  autosize: {
    // Needed to make an empty textarea have a height of one row
    append: true,
  },
};

export { jqueryUiConfig };
